<script setup lang="ts">
import { storeToRefs } from "pinia";
import { TrainingLmsAction } from "@simbelapp/order-sdk";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import Tooltip from "~/components/Common/Tooltip.vue";
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import DeleteSessionConfirmModal from "~/components/Pages/company-catalog/training-modal/steps/sessions/DeleteSessionConfirmModal.vue";
import { useCreateTrainingStore } from "~/store/trainings/create-training.store";
import { useTrainingLmsRules } from "~/composables/trainings/useTrainingLmsRules";
import { useTrainingCatalogSessions } from "~/composables/trainings/useTrainingCatalogSessions";
import { provideAddSessionFlow, useCatalogAddSessionFlow } from "~/composables/trainings/useAddSessionFlow";
import { SessionItemModalityEnum } from "~/utils/enums/session-items.enums";
import AddSessionModal from "./AddSessionModal.vue";
import CatalogSessionsSplit from "./CatalogSessionsSplit.vue";

const createTrainingStore = useCreateTrainingStore();
const { training, readonly } = storeToRefs(createTrainingStore);
const { isLmsActionDisabled } = useTrainingLmsRules(
  () => training.value.general.isExternalLms,
  () => training.value.general.lmsRules,
);
const { filterByEnabledType } = useTrainingCatalogSessions();
/** The wizard is shared with the internal modal: this is what it reads of an external training. */
provideAddSessionFlow(useCatalogAddSessionFlow());

const route = useRoute();

const showDeleteConfirmModal = ref(false);
const sessionId = ref<null | string>(null);

/** An inscription type unchecked on the Inscriptions tab hides its sessions here too. */
const enabledTypes = computed(() => ({
  individual: !!training.value.sessionItems.individual.isChecked,
  group: !!training.value.sessionItems.group.isChecked,
}));

const sessionsInPlan = computed(() =>
  filterByEnabledType(training.value.sessions.catalogSessionsInPlan, enabledTypes.value),
);
const sessionsNotInPlan = computed(() =>
  filterByEnabledType(training.value.sessions.catalogSessionsNotInPlan, enabledTypes.value),
);
const hasCatalogSessions = computed(() => !!sessionsInPlan.value.length || !!sessionsNotInPlan.value.length);

/** Self-paced trainings start whenever the participant wants: sessions are pointless there. */
const isOnDemandOnly = computed(
  () =>
    training.value.sessionItems.individual.modality === SessionItemModalityEnum.ON_DEMAND &&
    !training.value.sessionItems.group.isChecked,
);

const canAddSession = computed(
  () =>
    !training.value.sessions.showAddSession && !readonly.value && !isLmsActionDisabled(TrainingLmsAction.AddSession),
);
const isAddSessionLmsBlocked = computed(
  () =>
    !training.value.sessions.showAddSession && !readonly.value && isLmsActionDisabled(TrainingLmsAction.AddSession),
);

const deleteSession = (id: string) => {
  sessionId.value = id;
  showDeleteConfirmModal.value = true;
};

const hideDeleteConfirmModal = () => {
  sessionId.value = null;
  showDeleteConfirmModal.value = false;
};

const reloadSessions = async () => {
  // The store's id covers trainings opened without `?id=` (AI import); the route query backs deep links.
  const trainingId = createTrainingStore.trainingId ?? (route.query.id as string | undefined);
  if (trainingId) await createTrainingStore.loadSessions(trainingId);
};
</script>
<template>
  <div class="flex justify-between">
    <h3 class="heading_3_demi">Sessions</h3>
    <AppButton
      v-if="hasCatalogSessions && canAddSession"
      type="link"
      icon="add"
      label="Ajouter une session"
      @click="createTrainingStore.openAddSession"
    />
    <Tooltip v-else-if="hasCatalogSessions && isAddSessionLmsBlocked" :position="'top-mid'" minified append-to-body>
      <template #img>
        <div class="flex gap-1 items-center m-auto p-2 rounded-[12px] cursor-pointer text-medium-grey-light-color">
          <SvgIcon name="add" class="h-[16px] w-[16px]" />
          <span>Ajouter une session</span>
        </div>
      </template>
      <template #content>
        <div class="flex gap-[8px] items-center body_regular w-[338px]">
          <img src="/icons/emojis/eyes.svg" alt="" class="h-4 w-4" />
          <span>Cette action n'est pas disponible pour une session importée d'un LMS</span>
        </div>
      </template>
    </Tooltip>
  </div>

  <p class="mt-4 body_regular text-secondary-color">Seules les sessions en cours ou à venir sont affichées.</p>

  <div class="mt-4">
    <CatalogSessionsSplit
      v-if="hasCatalogSessions"
      :in-plan="sessionsInPlan"
      :not-in-plan="sessionsNotInPlan"
      :lms-delete-blocked="isLmsActionDisabled(TrainingLmsAction.RemoveSession)"
      :can-delete="!readonly"
      @delete-session="deleteSession"
    />

    <div v-else-if="isOnDemandOnly" class="flex flex-col">
      <span class="text-tertiary-color"
        >Il n’est pas nécessaire de créer des sessions car les participants peuvent commencer la formation en toute
        autonomie.</span
      >
      <div class="text-tertiary-color">
        <span
          >Si vous souhaitez pouvoir créer des sessions sur cette formation, veuillez modifier vos préférences
          concernant le choix date de début de la formation dans l’onglet</span
        ><span class="text-tertiary-color underline cursor-pointer ml-1" @click="createTrainingStore.nav = 2"
          >Inscriptions</span
        >.
      </div>
    </div>

    <div v-else class="flex flex-col gap-8">
      <span class="flex items-center text-tertiary-color h-10">Aucune session n’a été créée pour cette formation</span>
      <AppButton
        v-if="canAddSession"
        type="outlined"
        class="w-fit mx-auto"
        icon="add"
        label="Ajouter une session"
        @click="createTrainingStore.openAddSession"
      />
      <Tooltip v-else-if="isAddSessionLmsBlocked" :position="'top-mid'" minified append-to-body>
        <template #img>
          <div
            class="flex gap-1 items-center m-auto p-2 rounded-[12px] cursor-pointer border border-medium-grey-light-color text-medium-grey-light-color"
          >
            <SvgIcon name="add" class="h-[16px] w-[16px]" />
            <span>Ajouter une session</span>
          </div>
        </template>
        <template #content>
          <div class="flex gap-[8px] items-center body_regular w-[338px]">
            <img src="/icons/emojis/eyes.svg" alt="" class="h-4 w-4" />
            <span>Cette action n'est pas disponible pour une session importée d'un LMS</span>
          </div>
        </template>
      </Tooltip>
    </div>

    <DeleteSessionConfirmModal
      v-if="showDeleteConfirmModal && sessionId"
      :session-id="sessionId"
      @deleted="reloadSessions"
      @hide="hideDeleteConfirmModal"
    ></DeleteSessionConfirmModal>
  </div>
  <AddSessionModal v-if="training.sessions.showAddSession" />
</template>
